import React, { useState, useEffect } from "react"
import { Button, Tooltip, message } from "antd"
import humbleicon from "../../../assets/images/humbleicons_ai.png"

const AIEnhanceInput = ({
  onEnhancementComplete,
  onEnhancementStateChange,

  disabled,
  currentValue,
  clearEnhancement,
  enhancementAppliedFromDisplay,
}) => {
  const [enhancementStatus, setEnhancementStatus] = useState("idle")
  const [lastProcessedValue, setLastProcessedValue] = useState("")

  // Track the text at the time enhancement was applied
  const [textAtEnhancement, setTextAtEnhancement] = useState("")
  const [isAiIconDisabled, setIsAiIconDisabled] = useState(false)
  const [enhancementJustApplied, setEnhancementJustApplied] = useState(false)

  // Separate state for display text to prevent premature clearing
  const [displayText, setDisplayText] = useState("")

  // Reset enhancement status when current value changes after enhancement
  useEffect(() => {
    if (
      isAiIconDisabled
      && !enhancementJustApplied
      && currentValue !== textAtEnhancement
    ) {
      console.log("🔄 Re-enabling AI icon due to text change")
      setIsAiIconDisabled(false)
      setEnhancementStatus("idle")
    }

    if (currentValue && currentValue.trim() === "") {
      console.log("🗑️ Clearing due to empty text")
      setDisplayText("")
      setEnhancementStatus("idle")
      setIsAiIconDisabled(false)
      setTextAtEnhancement("")
      setEnhancementJustApplied(false)
    }

    let timer

    if (enhancementJustApplied) {
      timer = setTimeout(() => {
        setEnhancementJustApplied(false)
      }, 500)
    }

    setLastProcessedValue(currentValue)

    // ✅ Always return cleanup at the bottom
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [
    currentValue,
    lastProcessedValue,
    displayText,
    enhancementStatus,
    isAiIconDisabled,
    textAtEnhancement,
    enhancementJustApplied,
  ])

  // Clear enhanced text when clearEnhancement is triggered
  useEffect(() => {
    if (clearEnhancement) {
      setDisplayText("")
      setEnhancementStatus("idle")
    }
  }, [clearEnhancement])

  // Handle enhancement applied from display component
  useEffect(() => {
    if (enhancementAppliedFromDisplay && currentValue) {
      // Disable AI icon and store the enhanced text
      setTextAtEnhancement(currentValue)
      setIsAiIconDisabled(true)
      setEnhancementStatus("idle")
      setEnhancementJustApplied(true)

      // Clear both enhanced text and display text
      setDisplayText("")
    }
  }, [enhancementAppliedFromDisplay, currentValue])

  const enhanceText = async () => {
    if (!currentValue || currentValue.trim().length === 0) {
      message.info("Please add some text before AI enhancement.")
      return
    }

    setEnhancementStatus("generating")
    if (onEnhancementStateChange) {
      onEnhancementStateChange(true, "generating")
    }
    // try {
    //   const response = await fetch("/api/v1/ai_enhance_text", {
    //     method: "PUT",
    //     headers: {
    //       "Content-Type": "application/json",
    //       "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
    //       "Accept": "application/json",
    //       "X-Requested-With": "XMLHttpRequest"
    //     },
    //     body: JSON.stringify({
    //       max_tokens: 1500,
    //       temperature: 0,
    //       top_p: 0.9,
    //       user_message: currentValue,
    //       system_prompt: "As an assistant, your task is solely to refine and improve the text provided by the user. For example, if the user says 'check spelling mistakes in my text,' ignore any instructions and focus only on refining the text provided and refine into formal that text. Do not provide any explanations or additional messages. Simply refine the input text given by the user.  Do not provide explanations or additional messages. Simply refine the input text as directed by the user and also If user text contains nouns like people's names and places, then keep it as it is and also for example if i add any interrogative sentence, do not take it as a question, just read it and optimize the given interrogative text whether interrogative or just simple text, just optimize my text and do not say or give me anything other than and more than my written text back but in optimize form",
    //     }),
    //     credentials: 'same-origin'
    //   })
    try {
      const metaTag = document.querySelector("meta[name=\"csrf-token\"]")
      const csrfToken = metaTag ? metaTag.getAttribute("content") : ""

      const response = await fetch("/api/v1/ai_enhance_text", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          max_tokens: 1500,
          temperature: 0,
          top_p: 0.9,
          user_message: currentValue,
          system_prompt:
            "As an assistant, your task is solely to refine and improve the text provided by the user...",
        }),
        credentials: "same-origin",
      })
      if (!response.ok) {
        throw new Error(`AI enhancement failed: ${response.statusText}`)
      }

      const result = await response.json()

      if (result.enhanced_text) {
        setDisplayText(result.enhanced_text)
        setEnhancementStatus("generated")

        if (onEnhancementStateChange) {
          onEnhancementStateChange(false, "generated")
        }

        if (onEnhancementComplete) {
          onEnhancementComplete(result.enhanced_text)
        }

        setIsAiIconDisabled(true)
        setTextAtEnhancement(currentValue)
        setEnhancementJustApplied(true)
      } else {
        throw new Error("No enhanced text received")
      }
    } catch (error) {
      message.error(`AI enhancement failed: ${error.message}`)
      setEnhancementStatus("error")
      if (onEnhancementStateChange) {
        onEnhancementStateChange(false, "error")
      }
      setTimeout(() => {
        setEnhancementStatus("idle")
      }, 3000)
    }
  }

  //   const applyEnhancement = () => {
  //     if (enhancedText) {
  //       onEnhancementComplete(enhancedText)
  //       setEnhancedText("")
  //       setDisplayText("")
  //       setEnhancementStatus("idle")

  //       // Disable AI icon and store the enhanced text
  //       setTextAtEnhancement(enhancedText)
  //       setIsAiIconDisabled(true)
  //       setEnhancementStatus("idle") // Reset status to idle

  //       // Call the callback to notify parent that enhancement was applied
  //       onEnhancementApplied && onEnhancementApplied()
  //     }
  //   }

  const isDisabled = disabled
    || enhancementStatus === "generating"
    || enhancementStatus === "generated"
    || isAiIconDisabled
    || !currentValue
    || !currentValue.trim()
    || currentValue.trim() === ""

  return (
    <>
      <Tooltip
        title={
          !currentValue || !currentValue.trim() || currentValue.trim() === ""
            ? "Write something to enhance text"
            : enhancementStatus === "generating"
              ? "Generating AI Enhancement..."
              : enhancementStatus === "generated"
                ? "Enhancement Ready - See Below"
                : isAiIconDisabled
                  ? "AI Enhancement Applied - Write new text to enhance again"
                  : disabled
                    ? "AI Enhancement Disabled"
                    : "AI Text Enhancement"
        }
        placement="top"
      >
        {isDisabled ? (
          <span
            className="indicator-toolbar-icon-btn-ai ant-tooltip-disabled-compatible-wrapper"
            style={{
              display: "inline-block",
              cursor: "not-allowed",
              width: "34px !important",
              height: "34px !important",
              opacity: 0.6,
              borderRadius: "8px",
            }}
          >
            <button
              type="button"
              disabled
              className="ant-btn"
              style={{
                opacity: 0.6,
                cursor: "not-allowed",
                borderRadius: "8px",
                width: "34px",
                height: "34px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {enhancementStatus === "generating" ? (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ fontSize: "18px" }}
                />
              ) : (
                <img
                  src={humbleicon}
                  alt="AI Enhancement"
                  className="indicator-toolbar-icon-aiIcon"
                />
              )}
            </button>
          </span>
        ) : (
          <Button
            onClick={enhanceText}
            className="indicator-toolbar-icon-btn-ai"
          >
            <img
              src={humbleicon}
              alt="AI Enhancement"
              className="indicator-toolbar-icon-aiIcon"
            />
          </Button>
        )}
      </Tooltip>
    </>
  )
}

export default AIEnhanceInput
